import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@apollo/client/react';
import { KeyRound, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/Toast';
import { JOIN_RACE_BY_CODE } from '@/graphql/mutations';
import { extractErrorCode } from '@/lib/graphql-errors';

interface JoinByCodeModalProps {
    onClose: () => void;
}

export function JoinByCodeModal({ onClose }: JoinByCodeModalProps) {
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [joinRace, { loading }] = useMutation(JOIN_RACE_BY_CODE);

    function extractCode(input: string): string {
        const match = input.match(/\/join\/([A-Za-z0-9-]+)\/?$/);
        return match ? match[1] : input;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const trimmed = extractCode(code.trim());
        if (!trimmed) return;

        try {
            const { data } = await joinRace({ variables: { code: trimmed } });
            const race = data?.joinRaceByCode;
            if (race) {
                toast(t('quickActions.joinSuccess', { name: race.name }), 'success');
                onClose();
                navigate(`/races/${race.id}`);
            }
        } catch (err: unknown) {
            const errorCode = extractErrorCode(err);
            const messageKey = JOIN_ERROR_KEYS[errorCode] ?? 'quickActions.joinError.unknown';
            toast(t(messageKey), 'error');
        }
    }

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative z-10 p-6 animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500">
                                <KeyRound size={20} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">{t('quickActions.joinTitle')}</h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-400"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <p className="text-sm text-slate-500">{t('quickActions.joinDescription')}</p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={t('quickActions.joinPlaceholder')}
                            autoFocus
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder:text-slate-300"
                        />

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                            >
                                {tc('btn.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !code.trim()}
                                className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:text-slate-400 transition-colors shadow-lg shadow-emerald-200 active:scale-95 flex items-center justify-center gap-2"
                            >
                                {loading ? <Loader2 size={18} className="animate-spin" /> : t('quickActions.joinButton')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body,
    );
}

const JOIN_ERROR_KEYS: Record<string, string> = {
    NOT_FOUND: 'quickActions.joinError.notFound',
    VALIDATION_ERROR: 'quickActions.joinError.notPrivate',
    CONFLICT: 'quickActions.joinError.alreadyJoined',
};
